import { useCallback, useEffect, useState } from "react";

import "reactflow/dist/base.css";

import ReactFlow, {
  Background,
  useEdgesState,
  useNodesState,
  useReactFlow,
  useStoreApi,
} from "reactflow";
import CustomEdge from "../flow/Edge";
import { NodeDrawer } from "./CdpNodeDrawer";
import { VariableSchema } from "./VariableSchema";
import { CdpComponents } from "./variables/CdpComponents";

const edgeTypes = {
  step: CustomEdge,
};

const CdpFlow = ({ boxes, links, audience }) => {
  const { setViewport, setCenter } = useReactFlow();
  const store = useStoreApi();

  const [nodes, setNodes, onNodesChange] = useNodesState(boxes || []);
  const [edges, setEdges, onEdgesChange] = useEdgesState(links || []);
  const [reactOracleInstance, setReactOracleInstance] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [openNodeDrawer, setOpenNodeDrawer] = useState(false);
  const [prevNodes, setPrevNodes] = useState([]);

  const onEdgeUpdate = useCallback(
    (oldEdge, newConnection) =>
      setEdges((els) => {
        return els;
      }),
    []
  );

  useEffect(() => {
    setPrevNodes(nodes);
  }, [nodes]);

  const updateNode = (node) => {
    setNodes(
      nodes.map((n) => {
        if (n.id == node.id) {
          n.data = node.data;
        }
        return n;
      })
    );
  };

  const handleNodeClick = (e, node) => {
    const { action, target, data } = e.detail || {};
    if (action == "add") {
      const clause = VariableSchema.constructEmptyClause(node.data.type, {
        hide: true,
      });
      const scope = VariableSchema.constructEmptyScope({ items: [clause] });

      if (node.data.items.length == 0 || target == null) {
        node.data.items = [...(node.data.items || []), scope];
      } else {
        const vs = new VariableSchema(node.data.items || []);
        node.data.items = vs.apply_value(target, (c) => {
          c.items = [...(c.items || []), c.type == "scope" ? clause : scope];
          return c;
        });
      }

      updateNode(node);
      setOpenNodeDrawer(true);
      setSelectedNode(clause);
    } else if (action == "apply") {
      if (target == "node") {
        node.data = Object.assign(node.data, data);
      } else {
        const vs = new VariableSchema(node.data.items || []);
        node.data.items = vs.apply_value(target, data);
      }
      updateNode(node);
    } else if (action == "edit") {
      const vs = new VariableSchema(node.data.items || []);
      const clause = vs.findById(target);
      setOpenNodeDrawer(true);
      setSelectedNode(clause);
    } else if (action == "delete") {
      if (target == "node") {
        const _source = edges.find((e) => e.target == node.id);
        const _target = edges.find((e) => e.source == node.id);

        if (nodes.length <= 2 || !_target || !_source) {
          setNodes(nodes.filter((n) => node.id !== n.id));
          return;
        }

        const _new_edge = {
          source: _source.source,
          target: _target.target,
        };

        const _filtered_edges = (edges || []).filter((e) => {
          return ![e.target, e.source].includes(node.id);
        });

        const _nodes = [...nodes].filter((n) => node.id !== n.id);
        _nodes.sort((a, b) => a.position.x - b.position.x);
        setNodes(_nodes);
        setEdges([..._filtered_edges, _new_edge]);
      } else {
        const vs = new VariableSchema(node.data.items || []);
        node.data.items = vs.deleteById(target);
        updateNode(node);
      }
    }
  };

  return (
    <>
      <div style={{ height: "100%" }}>
        <ReactFlow
          ref={reactOracleInstance}
          snapGrid={[3, 3]}
          nodes={nodes}
          edges={edges}
          onEdgesChange={onEdgesChange}
          // onConnect={onConnect}
          onEdgeUpdate={onEdgeUpdate}
          elementsSelectable={true}
          nodeTypes={CdpComponents}
          edgeTypes={edgeTypes}
          onInit={setReactOracleInstance}
          nodesDraggable={false}
          onNodeClick={handleNodeClick}
        >
          <Background
            gap={50}
            style={{
              backgroundColor: "#191919",
            }}
            color="#4e5156"
          />
          <svg>
            <defs>
              <linearGradient id="edge-gradient">
                <stop offset="0%" stopColor="#191919" />
              </linearGradient>
            </defs>
          </svg>
        </ReactFlow>
      </div>

      <NodeDrawer
        visible={openNodeDrawer}
        clause={selectedNode}
        onClose={() => {
          setOpenNodeDrawer(false);
          setSelectedNode(null);
          const _nodes = [...nodes].map((n) => {
            const vs = new VariableSchema(n.data?.items || []);
            const [_items, count] = vs.clear((item) => {
              const { type, hide, items = [] } = item;
              const is_hide_clause = type != "scope" && hide == true;
              const is_empty_scope = type == "scope" && items?.length == 0;

              return is_hide_clause || is_empty_scope;
            });
            n.data.items = _items;
            return n;
          });
          setNodes(_nodes);
        }}
        onSave={(clause, close = true) => {
          const _nodes = [...nodes].map((n) => {
            const vs = new VariableSchema(n.data?.items || []);
            n.data.items = vs.apply_value(clause.id, (i) =>
              Object.assign(i, { ...clause, hide: false })
            );
            return n;
          });

          setNodes(_nodes);

          if (close) {
            setOpenNodeDrawer(false);
            setSelectedNode(null);
          }
        }}
        onRemove={(clause) => {
          setOpenNodeDrawer(false);
          const _nodes = [...nodes].map((n) => {
            const vs = new VariableSchema(n.data?.items || []);
            // Excluindo elemento da árvore
            vs.deleteById(clause.id);
            //Realizando a limpeza dos escopos vazios
            const [_items] = vs.clear();
            n.data.items = _items;
            return n;
          });
          setNodes(_nodes);
        }}
      />
    </>
  );
};

export default CdpFlow;
